import classNames from "classnames";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { ChatTeardropDots } from "components/icons/ChatTeardropDots";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { ReportFilter } from "components/Reports/ReportFilter";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import {
  ACCOUNTING_METHODS,
  DOWNLOAD_FORMAT,
  DOWNLOAD_FORMAT_TYPES,
  INCOME_STATEMENT,
} from "constants/bookkeeping";
import { PERCENTAGE_MODE_OPTIONS } from "constants/reports";
import { PERIOD } from "constants/revenueMetrics";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useQuery } from "hooks/useQuery";
import { parse, stringify } from "qs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  ANALYSIS_MODE,
  CHART_TYPE,
  CUSTOM_REPORT_MODE,
  useExportCustomReportChartMutation,
  useGetCustomReportByIdQuery,
  useGetCustomReportChartQuery,
  useGetCustomReportsQuery,
  useLazyGetCustomReportByIdQuery,
  useUpdateCustomReportMutation,
  type AnalysisModeType,
  type ChartConfig,
  type ChartType,
  type PreviewGroup,
} from "store/apis/customReports";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import { setOpenComment } from "store/slices/openComment";
import {
  CustomReportView,
  DEFAULT_CHART_CONFIG,
} from "pages/Books/Settings/NewCustomReportTemplate";
import type { ReportFilterType } from "components/Reports/ReportFilter";
import html2canvas from "html2canvas";
import { RefObject, useEffect, useRef, useState } from "react";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { openLink } from "utils/openLink";
import { BackendError } from "types/utils/error";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { Tag } from "components/DesignSystem/Tag/Tag";

const DEFAULT_FILTER: ReportFilterType = {
  START_DATE: dayjs().startOf("year").format(YYYY_MM_DD),
  END_DATE: dayjs().format(YYYY_MM_DD),
  ACCOUNTING_METHOD: ACCOUNTING_METHODS.ACCRUAL,
  VIEW_BY: null,
  HIDE_ZERO: true,
  TXN_GROUP_ID: null,
  CLASS_IDS: [],
  CLASSIFICATION: [],
  NO_CLASSIFICATION: [],
  OTHER: null,
  COMPARE_BY: null,
  VARIANCE: [],
  PERCENTAGE_MODE: null,
  FORECAST_ENABLED: false,
  FORECAST_START: "",
  FORECAST_END: "",
  FORECAST_SET_ID: null,
};

export const CustomReportDownload = ({
  reportId,
  isGroupByView,
  filter,
  disabled,
  isEditingReport = false,
  savePayload,
  onSaved,
  chartRef,
  isChartReady,
}: {
  reportId: string;
  isGroupByView?: boolean;
  filter?: ReportFilterType;
  disabled?: boolean;
  isEditingReport?: boolean;
  savePayload?: {
    title: string;
    groups: PreviewGroup[];
    chartConfig: ChartConfig;
  };
  onSaved?: () => void;
  chartRef?: RefObject<HTMLDivElement | null>;
  isChartReady?: boolean;
}) => {
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const { link } = useConstructInternalLink();

  const [updateCustomReport, { isLoading: isSaving }] =
    useUpdateCustomReportMutation();

  const [downloadReport, { isLoading, isFetching }] =
    useLazyGetCustomReportByIdQuery();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [exportCustomReportChart] = useExportCustomReportChartMutation();

  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();

  const [selectedFormat, setSelectedFormat] = useState<DOWNLOAD_FORMAT_TYPES>();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();
  const [isChartExporting, setIsChartExporting] = useState(false);
  const [isChartFolderExport, setIsChartFolderExport] = useState(false);

  const isDownloading = isLoading || isFetching || isSaving || isChartExporting;

  const onSaveInInkleDocs = (format: DOWNLOAD_FORMAT_TYPES) => {
    setIsChartFolderExport(false);
    setSelectedFormat(format);
    selectFolderModal.open();
  };

  const onSaveChartInInkleDocs = () => {
    setIsChartFolderExport(true);
    selectFolderModal.open();
  };

  const handleChartExport = async (
    exportToMail?: boolean,
    folderId?: string
  ) => {
    if (!isChartReady) {
      alertToast({ message: "Chart is not ready yet, please try again" });
      return;
    }

    setIsChartExporting(true);
    try {
      if (isEditingReport && savePayload) {
        await updateCustomReport({
          entityId,
          reportId,
          payload: {
            title: savePayload.title,
            start_date: filter?.START_DATE,
            end_date: filter?.END_DATE,
            accounting_method: filter?.ACCOUNTING_METHOD,
            comparison_report_type: filter?.VIEW_BY,
            percentage_mode: filter?.PERCENTAGE_MODE,
            chart_config: savePayload.chartConfig,
            groups: savePayload.groups.map(({ uuid, title, category_ids }) => ({
              uuid,
              title,
              category_ids,
            })),
          },
        }).unwrap();
        onSaved?.();
      }

      const capturePromise = html2canvas(chartRef!.current!, {
        scale: 1,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("html2canvas timed out")), 30_000)
      );
      const canvas = await Promise.race([capturePromise, timeoutPromise]);
      const graph = canvas.toDataURL("image/png").split(",")[1];

      const { document_id } = await exportCustomReportChart({
        entityId,
        reportId,
        graphs: [graph],
        export_to_mail: exportToMail,
        folder_id: folderId,
        start_date: filter?.START_DATE,
        end_date: filter?.END_DATE,
      }).unwrap();

      if (folderId) {
        setSavedDocumentId(document_id);
        savedToInkleDocsModal.open();
        return;
      }

      if (exportToMail) {
        successToast({ message: "Chart will be sent to your email" });
        return;
      }

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url);
      successToast({ message: "Chart exported successfully" });
      if (isEditingReport && savePayload) {
        history.push(link(`/books/live-financials/custom-report/${reportId}`));
      }
    } catch {
      alertToast({ message: "Failed to export chart" });
    } finally {
      setIsChartExporting(false);
    }
  };

  const onDownload = async (
    downloadType: DOWNLOAD_FORMAT_TYPES,
    exportToMail?: boolean,
    folderId?: string
  ) => {
    try {
      let document_id: string | undefined;

      if (isEditingReport && savePayload) {
        const response = await updateCustomReport({
          entityId,
          reportId,
          payload: {
            title: savePayload.title,
            start_date: filter?.START_DATE,
            end_date: filter?.END_DATE,
            accounting_method: filter?.ACCOUNTING_METHOD,
            comparison_report_type: filter?.VIEW_BY,
            percentage_mode: filter?.PERCENTAGE_MODE,
            chart_config: savePayload.chartConfig,
            groups: savePayload.groups.map(({ uuid, title, category_ids }) => ({
              uuid,
              title,
              category_ids,
            })),
            action: downloadType,
            export_to_mail: exportToMail,
            folder_id: folderId,
          },
        }).unwrap();
        document_id = response.document_id;
        onSaved?.();
      } else {
        const response = await downloadReport({
          entityId,
          reportId,
          action: downloadType,
          export_to_mail: exportToMail,
          folderId,
          accountingMethod: filter?.ACCOUNTING_METHOD,
          startDate: filter?.START_DATE || null,
          endDate: filter?.END_DATE || null,
          showNonZeroAccounts: filter?.HIDE_ZERO,
          comparisonReportType: filter?.VIEW_BY || "TOTALS",
          percentageMode: filter?.PERCENTAGE_MODE,
        }).unwrap();
        document_id = response.document_id;
      }

      if (folderId) {
        setSavedDocumentId(document_id);
        savedToInkleDocsModal.open();
        return;
      }

      if (exportToMail) {
        successToast({ message: "Report will be sent to your email" });
        return;
      }

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url);
      successToast({ message: "Report downloaded" });
      if (isEditingReport && savePayload) {
        history.push(link(`/books/live-financials/custom-report/${reportId}`));
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger asChild disabled={disabled || isDownloading}>
          <div>
            {isEditingReport ? (
              <Button
                customType="secondary"
                size="small"
                isLoading={isDownloading}
                disabled={disabled || isDownloading}
              >
                <div className="t-flex t-items-center t-gap-1">
                  Save & Export
                  <ArrowDown color="currentColor" strokeWidth="1.6" />
                </div>
              </Button>
            ) : (
              <Button
                customType="primary"
                size="small"
                isLoading={isDownloading}
                disabled={disabled || isDownloading}
              >
                <div className="t-flex t-items-center t-gap-1">
                  Export
                  <ArrowDown color="white" strokeWidth="1.6" />
                </div>
              </Button>
            )}
          </div>
        </Dropdown.Trigger>

        <Dropdown.Portal>
          <Dropdown.Content sideOffset={4} align="end">
            {chartRef && (
              <Dropdown.Sub>
                <Dropdown.SubTrigger>
                  <div className="t-flex t-items-center t-gap-2">
                    Export Chart
                    <Tag tagType="purple" size="small" rounded icon={false}>
                      PDF
                    </Tag>
                  </div>
                </Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent>
                    <ExportOptions
                      onLocalDownload={() => handleChartExport(false)}
                      onMailExport={() => handleChartExport(true)}
                      onSaveInInkleDocs={onSaveChartInInkleDocs}
                    />
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
            )}
            <Dropdown.Sub>
              <Dropdown.SubTrigger>Export Table</Dropdown.SubTrigger>
              <Dropdown.Portal>
                <Dropdown.SubContent>
                  <ConditionalToolTip
                    side="left"
                    condition={
                      isGroupByView &&
                      "PDF export is not available for this view"
                    }
                    sideOffset={4}
                  >
                    <span>
                      <Dropdown.Sub>
                        <Dropdown.SubTrigger
                          disabled={isGroupByView}
                          className={classNames({
                            "t-opacity-50 t-cursor-not-allowed": isGroupByView,
                          })}
                        >
                          PDF
                        </Dropdown.SubTrigger>
                        <Dropdown.Portal>
                          <Dropdown.SubContent>
                            <ExportOptions
                              onLocalDownload={() =>
                                onDownload(DOWNLOAD_FORMAT.DOWNLOAD_PDF, false)
                              }
                              onMailExport={() =>
                                onDownload(DOWNLOAD_FORMAT.DOWNLOAD_PDF, true)
                              }
                              onSaveInInkleDocs={() =>
                                onSaveInInkleDocs(DOWNLOAD_FORMAT.DOWNLOAD_PDF)
                              }
                            />
                          </Dropdown.SubContent>
                        </Dropdown.Portal>
                      </Dropdown.Sub>
                    </span>
                  </ConditionalToolTip>
                  <Dropdown.Sub>
                    <Dropdown.SubTrigger>Excel</Dropdown.SubTrigger>
                    <Dropdown.Portal>
                      <Dropdown.SubContent>
                        <ExportOptions
                          onLocalDownload={() =>
                            onDownload(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, false)
                          }
                          onMailExport={() =>
                            onDownload(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, true)
                          }
                          onSaveInInkleDocs={() =>
                            onSaveInInkleDocs(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL)
                          }
                        />
                      </Dropdown.SubContent>
                    </Dropdown.Portal>
                  </Dropdown.Sub>
                </Dropdown.SubContent>
              </Dropdown.Portal>
            </Dropdown.Sub>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => {
            if (isChartFolderExport) {
              handleChartExport(false, folderId);
            } else {
              onDownload(selectedFormat!, false, folderId);
            }
          }}
        />
      )}

      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="CUSTOM_REPORT"
        />
      )}
    </>
  );
};

const CustomReportViewOnlyContent = ({
  initialFilter,
  chartConfig,
}: {
  initialFilter: ReportFilterType;
  chartConfig: {
    chartType: ChartType;
    analysisMode: AnalysisModeType;
    seriesGroupId: string | null;
    seriesMode: string;
  };
}) => {
  const { link } = useConstructInternalLink();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { reportId } = useParams<{ reportId: string }>();
  const commentPane = useModal();
  const openComment = useAppSelector((store) => store.openComment.openComment);
  const query = useQuery();
  const selectedCategoryId = query.get("selected_category_id");
  const openCommentParam = query.get("open_comment");

  const { values: filter, updateFilter } = useFilters({
    initialValue: initialFilter,
  });

  const chartRef = useRef<HTMLDivElement | null>(null);

  const { data: chartResponse, isFetching: isChartFetching } =
    useGetCustomReportChartQuery(
      {
        entityId,
        reportId,
        startDate: filter.START_DATE,
        endDate: filter.END_DATE,
        accountingMethod: filter.ACCOUNTING_METHOD,
        comparisonReportType: filter.VIEW_BY,
        showNonZeroAccounts: filter.HIDE_ZERO,
        chartType: chartConfig.chartType,
        analysisMode: chartConfig.analysisMode,
        seriesGroupId: chartConfig.seriesGroupId,
        seriesMode: chartConfig.seriesMode,
      },
      { skip: !reportId }
    );

  const {
    data: customReport,
    isLoading,
    isFetching,
  } = useGetCustomReportByIdQuery(
    {
      entityId,
      reportId,
      startDate: filter.START_DATE,
      endDate: filter.END_DATE,
      accountingMethod: filter.ACCOUNTING_METHOD,
      comparisonReportType: filter.VIEW_BY || "TOTALS",
      showNonZeroAccounts: filter.HIDE_ZERO,
      percentageMode: filter.PERCENTAGE_MODE,
    },
    { skip: !reportId }
  );

  const isLiveFinancialsContext = location.pathname.includes("live-financials");

  const customReportsListUrl = isLiveFinancialsContext
    ? link("/books/live-financials/others")
    : link("/books/custom-reports");

  const breadcrumbName = isLiveFinancialsContext
    ? "Report List"
    : "Custom Reports";

  const editBasePath = isLiveFinancialsContext
    ? "/books/live-financials/custom-report/new"
    : "/books/custom-reports/new";

  const handleEdit = () => {
    history.push(
      link(editBasePath, {
        moreQuery: { reportId, mode: CUSTOM_REPORT_MODE.EDIT },
      })
    );
  };

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.category) {
      const { uuid, group_id } = comment.category;
      const compositeId = group_id ? `${group_id}-${uuid}` : uuid;
      dispatch(setOpenComment(compositeId));
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (selectedCategoryId) {
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.selected_category_id;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [selectedCategoryId]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  const reportFilters = {
    reportType: INCOME_STATEMENT,
    accountingMethod: filter.ACCOUNTING_METHOD,
    startDate: filter.START_DATE,
    endDate: filter.END_DATE,
    showNonZeroAccounts: filter.HIDE_ZERO,
    comparison_report_type: filter.VIEW_BY,
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <DashboardContainer className="t-h-full t-w-full t-flex t-px-5 sm:t-px-3 sm:t-h-auto sm:t-overflow-x-visible">
      <DashboardContainer.Header className="t-sticky t-top-0 t-bg-white sm:t-static">
        <Header
          v2
          title={customReport?.title || "Custom Report"}
          breadcrumbs={[{ name: breadcrumbName, link: customReportsListUrl }]}
          right={
            <div className="t-flex t-items-center t-gap-2">
              <Button
                customType="icon"
                size="small"
                onClick={() => commentPane.toggle()}
              >
                <ChatTeardropDots />
              </Button>
              <LockedUi
                feature={WRITE_PERMISSION_KEYS.FINANCIALS}
                blockedUi={<LockedButton text="Edit" />}
              >
                <Button
                  customType="secondary"
                  size="small"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </LockedUi>
              {reportId && (
                <CustomReportDownload
                  reportId={reportId}
                  isGroupByView={
                    filter.VIEW_BY === PERIOD.MONTHLY ||
                    filter.VIEW_BY === PERIOD.QUARTERLY ||
                    filter.VIEW_BY === PERIOD.YEARLY
                  }
                  filter={filter}
                  chartRef={chartRef}
                  isChartReady={!isChartFetching && !!chartResponse}
                />
              )}
            </div>
          }
        />
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-flex t-flex-col t-flex-1 t-overflow-hidden t-min-h-0 sm:t-overflow-visible">
        <div className="t-py-4 sm:t-flex sm:t-flex-wrap sm:t-items-center sm:t-gap-2">
          <ReportFilter
            reportType={INCOME_STATEMENT}
            filter={filter}
            updateFilter={updateFilter}
            entityId={entityId}
            isGroupByView={false}
            showClassificationInFilters={false}
            hideViewBy
          />
        </div>
        <div
          className={classNames(
            "t-flex t-flex-1 t-min-h-0 t-overflow-hidden sm:t-overflow-visible",
            {
              "t-pr-1.5": commentPane.isOpen,
              "t-pr-0": !commentPane.isOpen,
            }
          )}
        >
          <div className="t-flex-1 t-h-full t-overflow-y-auto t-pb-20 sm:t-h-auto sm:t-overflow-visible">
            {isFetching || isChartFetching ? (
              <Loader />
            ) : (
              <CustomReportView
                periods={customReport?.periods}
                showComments
                groupBy={filter.VIEW_BY}
                percentageMode={filter.PERCENTAGE_MODE}
                reportId={reportId}
                chartData={chartResponse!}
                chartRef={chartRef}
              />
            )}
          </div>
          <AnimatePresence>
            {commentPane.isOpen && (
              <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
                <CommentsSlider
                  setActiveComment={setActiveComment}
                  activeComment={openComment}
                  onClose={() => commentPane.toggle()}
                  commentTypes={[COMMENT_TYPE.CUSTOM_REPORT_COMMENT]}
                  reportFilters={reportFilters}
                />
              </SliderAnimation>
            )}
          </AnimatePresence>
        </div>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};

export const CustomReportViewOnly = () => {
  const entityId = useCurrentEntityId();
  const { reportId } = useParams<{ reportId: string }>();

  const { data: customReportsList, isLoading: isCustomReportsListLoading } =
    useGetCustomReportsQuery({ entityId }, { skip: !entityId });

  const reportConfig = customReportsList?.find((r) => r.uuid === reportId);

  if (isCustomReportsListLoading || !reportConfig) {
    return <Loader />;
  }

  const initialFilter: ReportFilterType = {
    ...DEFAULT_FILTER,
    START_DATE: reportConfig?.start_date
      ? dayjs(reportConfig.start_date).format(YYYY_MM_DD)
      : DEFAULT_FILTER.START_DATE,
    END_DATE: reportConfig?.end_date
      ? dayjs(reportConfig.end_date).format(YYYY_MM_DD)
      : DEFAULT_FILTER.END_DATE,
    ACCOUNTING_METHOD:
      (reportConfig?.accounting_method as ReportFilterType["ACCOUNTING_METHOD"]) ||
      DEFAULT_FILTER.ACCOUNTING_METHOD,
    VIEW_BY:
      (reportConfig?.comparison_report_type as ReportFilterType["VIEW_BY"]) ||
      null,
    PERCENTAGE_MODE: reportConfig?.percentage_mode || null,
  };

  return (
    <CustomReportViewOnlyContent
      initialFilter={initialFilter}
      chartConfig={{
        chartType:
          reportConfig.chart_config?.chart_type ??
          DEFAULT_CHART_CONFIG.chart_type,
        analysisMode:
          reportConfig.chart_config?.y_axis ?? DEFAULT_CHART_CONFIG.y_axis,
        seriesGroupId:
          reportConfig.chart_config?.series?.group_id ??
          DEFAULT_CHART_CONFIG.series.group_id,
        seriesMode:
          reportConfig.chart_config?.series?.mode ??
          DEFAULT_CHART_CONFIG.series.mode,
      }}
    />
  );
};
